import { Star, Pin } from "lucide-react";

// Tier mapping for fit-score badges
function tierFor(score) {
    if (score >= 90) return { color: "#10B981", label: "EXCEPTIONAL", tone: "green" }; // emerald
    if (score >= 80) return { color: "#3B82F6", label: "STRONG", tone: "blue" };
    if (score >= 70) return { color: "#FACC15", label: "GOOD", tone: "yellow" };
    return null;
}

// Shows the best fit-score star for a firm; on hover lists all relevant partners
export function FitBadge({ firmId, synergies = [], allFirms = [] }) {
    const related = synergies.filter((s) => s.firm_a_id === firmId || s.firm_b_id === firmId);
    if (!related.length) return null;

    // Best by score
    const best = related.reduce((a, b) => (b.fit_score > a.fit_score ? b : a), related[0]);
    const tier = tierFor(best.fit_score);
    const hasPinned = related.some((s) => s.pinned);

    // Nothing to show if neither score qualifies nor any pinned
    if (!tier && !hasPinned) return null;

    const display = tier
        ? { color: tier.color, icon: <Star className="w-3 h-3" style={{ color: tier.color, fill: tier.color }} />, text: best.fit_score }
        : { color: "#A1A1AA", icon: <Pin className="w-3 h-3" style={{ color: "#A1A1AA" }} />, text: "PIN" };

    return (
        <div className="relative group" data-testid={`fit-badge-${firmId}`}>
            <div className="inline-flex items-center gap-1 px-1.5 py-0.5 border" style={{ borderColor: `${display.color}66`, background: `${display.color}1A` }}>
                {display.icon}
                <span className="font-mono text-[10px] tracking-wider" style={{ color: display.color }}>{display.text}</span>
            </div>
            <div className="absolute z-10 right-0 top-full mt-1 hidden group-hover:block bg-zinc-950 border border-zinc-800 shadow-xl p-3 min-w-[240px]">
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Saved synergies</div>
                <ul className="space-y-1.5">
                    {related
                        .slice()
                        .sort((a, b) => b.fit_score - a.fit_score)
                        .slice(0, 6)
                        .map((s) => {
                            const otherId = s.firm_a_id === firmId ? s.firm_b_id : s.firm_a_id;
                            const other = allFirms.find((f) => f.id === otherId);
                            const otherName = other?.name || (s.firm_a_id === firmId ? s.firm_b_name : s.firm_a_name);
                            const t = tierFor(s.fit_score);
                            return (
                                <li key={`${s.firm_a_id}-${s.firm_b_id}-${s.product_id}`} className="flex items-center justify-between gap-2 text-xs">
                                    <span className="text-zinc-200 truncate inline-flex items-center gap-1">
                                        {s.pinned && <Pin className="w-2.5 h-2.5 text-amber-500" />}
                                        {otherName}
                                    </span>
                                    <span className="font-mono" style={{ color: t?.color || "#A1A1AA" }}>{s.fit_score}</span>
                                </li>
                            );
                        })}
                </ul>
            </div>
        </div>
    );
}

export function FitLegend() {
    return (
        <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-wider text-zinc-500" data-testid="fit-legend">
            <span className="inline-flex items-center gap-1">
                <Star className="w-3 h-3" style={{ color: "#FACC15", fill: "#FACC15" }} /> 70+
            </span>
            <span className="inline-flex items-center gap-1">
                <Star className="w-3 h-3" style={{ color: "#3B82F6", fill: "#3B82F6" }} /> 80+
            </span>
            <span className="inline-flex items-center gap-1">
                <Star className="w-3 h-3" style={{ color: "#10B981", fill: "#10B981" }} /> 90+
            </span>
        </div>
    );
}
